import { useEffect, useRef } from "react";

/**
 * Wires a modal's open state to the browser history so the phone / browser
 * Back button closes the modal instead of navigating away from the current
 * page. Call inside a component with a controlled `open` state.
 */

// Track popstate events to ignore (fired by our own history.back() calls)
let ignoreNextPop = 0;

// Every modal currently open. When a modal-to-modal transition happens (e.g.
// user clicks Edit inside a Details dialog), the closing modal defers its
// history cleanup by one microtask. If another modal opens before that
// microtask runs it "adopts" the pending history entry, so the browser's
// history stack never grows or shrinks during modal transitions.
let pendingCleanup = null;

export function useModalBack(open, onClose) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || typeof window === "undefined") return undefined;

    let closedByBack = false;
    let ownedEntry = false;

    if (pendingCleanup) {
      // Adopt the history entry the previous modal was about to unwind.
      pendingCleanup.cancelled = true;
      pendingCleanup = null;
      ownedEntry = true; // we now own the existing __eliteModal state
    } else {
      try {
        window.history.pushState({ __eliteModal: Date.now() + Math.random() }, "");
        ownedEntry = true;
      } catch (e) {
        ownedEntry = false;
      }
    }

    const onPop = () => {
      if (ignoreNextPop > 0) {
        ignoreNextPop--;
        return;
      }
      closedByBack = true;
      onCloseRef.current?.();
    };
    window.addEventListener("popstate", onPop);

    return () => {
      window.removeEventListener("popstate", onPop);
      if (closedByBack || !ownedEntry) return;

      // Defer the history.back() so a sibling modal that opens in the same
      // render tick can adopt this entry instead of us popping and them
      // pushing (which grows/shrinks the stack asymmetrically).
      const record = { cancelled: false };
      pendingCleanup = record;
      queueMicrotask(() => {
        if (record.cancelled) return;
        pendingCleanup = null;
        try {
          if (window.history.state && window.history.state.__eliteModal) {
            ignoreNextPop += 1;
            window.history.back();
          }
        } catch (e) {
          /* ignore */
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
